import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  LoadingSpinner,
  notificationTypes,
  PageNotification,
  SiteNav
} from '@animoto/components/lib/es/design-system';
import './PlayApp.module.css';
import { trackFeatureUsage, trackPage } from '@animoto/utils/analytics';
import { useMediaQuery } from 'react-responsive';
import classNames from 'classnames';
import evaluateFeature from '@animoto/ld-utils/evaluateFeature';

import aspectRatios from '../../../../configs/enums/aspectRatios';
import Player from '../../../../components/Player';
import redirect from '../../../../utils/redirect';
import { getUserAttribute } from '../../../../utils/userWrapper';
import getVideoSources from '../../../../utils/getVideoSources';
import pollRenderJob from '../../../../utils/api/pollRenderJob';
import setupTracking from '../../utils/tracking';
import Banner from '../Banner';
import BlogPosts from '../BlogPosts';
import VideoSteps from '../VideoSteps';
import VideoDetails from '../VideoDetails';
import ShareModal from '../ShareModal';
import OwnerToast from '../OwnerToast';
import Comments from '../Comments';

export default function PlayApp({
  aspectRatio,
  autostart,
  coverImageUrl,
  ctaTitle,
  ctaUrl,
  defaultResolution,
  description,
  displayDate,
  isAndroid,
  isIOS,
  isMobile,
  isRegisteredUser,
  isVideoOwner,
  producerName,
  showSocialNetworks,
  title,
  renderData,
  rootRenderOkey,
  projectOkey
}) {
  const { video_formats : videoFormats, jobs : renderJobs } = renderData;
  const [hasVideoCompleted, setHasVideoCompleted] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [sources, setSources] = useState(getVideoSources(videoFormats, defaultResolution));
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const videoPlayerRef = useRef(null);
  const isAbove575 = useMediaQuery({ query : '(min-width: 575px)' });
  const isAbove900 = useMediaQuery({ query : '(min-width: 900px)' });
  const isMiddleSize = !isAbove900 && isAbove575;
  const isMobileSize = isMobile || !isAbove575;
  const isVideoRendering = !sources.length;
  const resolution = isVideoRendering ? '0p' : sources.find((source) => source.selected).label;
  const playPageUrl = window.location.href;

  if (isVideoRendering && renderJobs.length) {
    let renderJob = renderJobs.find(({ purpose }) => purpose === 'preview');
    if (!renderJob) {
      renderJob = renderJobs.find(({ state }) => state === 'rendering');
    }

    pollRenderJob(renderJob.links.self, (completedJob) => {
      setSources(getVideoSources([completedJob]));
    }, () => {
      setErrorMessage('Sorry, there was an error loading the video. Please try again later.');
    });
  }

  React.useEffect(() => {
    trackPage('/play/visitor', true);
  }, []);

  const playerSettings = {
    autostart,
    poster  : coverImageUrl,
    sources,
    preload : 'auto'
  };

  if (ctaTitle && ctaUrl) {
    playerSettings.ctaSettings = {
      title : ctaTitle,
      url   : ctaUrl
    };
  }

  if (aspectRatio === aspectRatios.SQUARE) {
    playerSettings.aspectRatio = '1:1';
  } else if (aspectRatio === aspectRatios.VERTICAL) {
    playerSettings.aspectRatio = '9:16';
  }

  function onCreatePlayer(videoPlayer) {
    videoPlayerRef.current = videoPlayer;

    videoPlayer.once('playlistComplete', () => {
      setHasVideoCompleted(true);
    });

    setupTracking(videoPlayer);
  }

  const videoStyles = classNames('video-wrapper', aspectRatio);

  const onClickButton = () => {
    if (!isMobile && isRegisteredUser) {
      window.open('/builder/templates');
    } else if (!isMobile && !isRegisteredUser) {
      window.open('/sign_up');
    } else if (isAndroid) {
      redirect('/sign_up');
    }
  };

  const openMobileSharePopup = async () => {
    try {
      trackFeatureUsage({
        scope   : 'visitor-play-page',
        feature : 'share-mobile',
        action  : 'open'
      });

      await window.navigator.share({
        title,
        url : playPageUrl
      });

      trackFeatureUsage({
        scope   : 'visitor-play-page',
        feature : 'share-mobile',
        action  : 'close',
        detail  : 'complete'
      });
    } catch (e) {
      trackFeatureUsage({
        scope   : 'visitor-play-page',
        feature : 'share-mobile',
        action  : 'close',
        detail  : 'cancel'
      });
    }
  };

  const onOpenShareModal = () => {
    if (isMobile) {
      openMobileSharePopup();
    } else {
      setIsShareModalOpen(true);
    }
  };

  const onCloseShareModal = () => {
    setIsShareModalOpen(false);
  };

  return (
    <div styleName="wrapper">
      <SiteNav
        firstName={getUserAttribute('first_name')}
        isLoggedIn={isRegisteredUser}
        lastName={getUserAttribute('last_name')}
      />
      { errorMessage && (
        <PageNotification autoHide type={notificationTypes.ERROR}>
          {errorMessage}
        </PageNotification>
      )}
      {!isMobile && (
        <ShareModal
          isOpen={isShareModalOpen}
          onCloseModal={onCloseShareModal}
          producerName={producerName}
          resolution={resolution}
          shareUrl={playPageUrl}
          showSocialNetworks={showSocialNetworks}
          videoTitle={title}
        />
      )}
      <div styleName="content-wrap">
        <div styleName="blog-styles">
          <div styleName="container">
            <div styleName="video-wrapper-wrap">
              <div className="s-video" styleName={videoStyles}>
                {isVideoRendering ? (
                  <LoadingSpinner size="large" />
                ) : (
                  <Player onCreatePlayer={onCreatePlayer} settings={playerSettings} />
                )}
              </div>
            </div>
            { isAbove900 && (
              <div styleName="blog">
                <BlogPosts
                  isMiddleSize={isMiddleSize}
                />
              </div>
            )}
          </div>
          {evaluateFeature('prototype-commenting') && (
            <Comments projectOkey={projectOkey} rootRenderOkey={rootRenderOkey} videoPlayerRef={videoPlayerRef} />
          )}
          <div styleName="column-wrapper">
            <div styleName="details">
              {!isMobile && (
                <Banner
                  hasVideoCompleted={hasVideoCompleted}
                  isIOS={isIOS}
                  isMobile={isMobileSize}
                  onClick={onClickButton}
                />
              )}
              <VideoDetails
                description={description}
                displayDate={displayDate}
                isMobile={isMobileSize}
                isSharingEnabled={!isVideoRendering}
                onOpenShareModal={onOpenShareModal}
                producerName={producerName}
                title={title}
              />
            </div>
            <div styleName="explore-animoto-wrap">
              {!isAbove900 && (
                <div styleName="blog">
                  <BlogPosts
                    isMiddleSize={isMiddleSize}
                  />
                </div>
              )}
              <VideoSteps
                isIOS={isIOS}
                isMobile={isMobileSize}
                onClick={onClickButton}
              />
            </div>
          </div>
          {isMobile && (
            <Banner
              hasVideoCompleted={hasVideoCompleted}
              isIOS={isIOS}
              isMobile={isMobileSize}
              onClick={onClickButton}
            />
          )}
        </div>
      </div>
      {isVideoOwner && <OwnerToast />}
    </div>
  );
}

PlayApp.propTypes = {
  aspectRatio       : PropTypes.string.isRequired,
  autostart         : PropTypes.bool,
  coverImageUrl     : PropTypes.string,
  ctaTitle          : PropTypes.string,
  ctaUrl            : PropTypes.string,
  defaultResolution : PropTypes.string,
  description       : PropTypes.string,
  displayDate       : PropTypes.string.isRequired,
  isAndroid         : PropTypes.bool.isRequired,
  isIOS             : PropTypes.bool.isRequired,
  isMobile          : PropTypes.bool.isRequired,
  isRegisteredUser  : PropTypes.bool.isRequired,
  isVideoOwner      : PropTypes.bool.isRequired,
  producerName      : PropTypes.string,
  // TODO: remove when prototype-commenting is done
  projectOkey       : PropTypes.string,
  renderData        : PropTypes.shape({
    jobs          : PropTypes.array, // eslint-disable-line react/forbid-prop-types
    video_formats : PropTypes.array // eslint-disable-line react/forbid-prop-types
  }).isRequired,
  // TODO: remove when prototype-commenting is done
  rootRenderOkey     : PropTypes.string,
  showSocialNetworks : PropTypes.bool,
  title              : PropTypes.string.isRequired
};

PlayApp.defaultProps = {
  autostart          : false,
  coverImageUrl      : '',
  ctaTitle           : '',
  ctaUrl             : '',
  defaultResolution  : '',
  description        : '',
  producerName       : '',
  // TODO: remove when prototype-commenting is done
  projectOkey        : '',
  rootRenderOkey     : '',
  showSocialNetworks : false
};
